import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import IncidentsSectionHeader from "../status/incidents-section-header";
import IncidentsTable from "../status/incidents-table";
import useIncidentsFilter from "../status/use-incidents-filter";
import { Incident } from "../status/data/monitoring-service";
import { setTaskAsCompleted } from "./task-info-service";
import { LongRunningTaskModel } from "./types";
import CompleteLongRunningTaskDialog from "./complete-long-running-task-dialog";
import { mapLongRunningTasksToIncidents } from "./long-running-task-incident-mapper";
import { useNotification } from "../../context/notification-provider";
import {
  getCompleteTaskErrorMessage,
  getCompleteTaskSuccessMessage,
} from "./task-completion-messages";

interface LongRunningTasksStatusProps {
  data: LongRunningTaskModel[] | undefined;
  isLoading: boolean;
  error: unknown;
  onComplete: () => Promise<void>;
}

const LongRunningTasksStatus = ({
  data,
  isLoading,
  error,
  onComplete,
}: LongRunningTasksStatusProps) => {
  const { showNotification } = useNotification();
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<LongRunningTaskModel | null>(
    null,
  );
  const searchQuery = "";
  const statusFilter = "active";
  const severityFilter = "all";
  const sortBy = "oldest";

  const incidents = useMemo(() => mapLongRunningTasksToIncidents(data), [data]);
  const filteredAndSortedIncidents = useIncidentsFilter({
    incidents,
    searchQuery,
    statusFilter,
    severityFilter,
    sortBy,
  });

  const handleOpenCompleteDialog = (incident: Incident) => {
    const task = data?.find((item) => item.task.id === incident.guid) ?? null;
    setSelectedTask(task);
  };

  const handleCloseCompleteDialog = () => {
    setSelectedTask(null);
  };

  const handleCompleteTask = async () => {
    const task = selectedTask;

    if (!task) return;

    setCompletingTaskId(task.task.id);
    try {
      const response = await setTaskAsCompleted({
        id: task.task.id,
        tableId: task.tableInformation.id,
      });
      showNotification({
        message: getCompleteTaskSuccessMessage(response),
        severity: "success",
        duration: 3000,
      });
      await onComplete();
    } catch (completeError) {
      showNotification({
        message: getCompleteTaskErrorMessage(completeError),
        severity: "error",
        duration: 3000,
      });
    } finally {
      setCompletingTaskId(null);
      setSelectedTask(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load long running tasks.</Alert>;
  }

  return (
    <Box sx={{ height: "100%", overflow: "hidden" }}>
      <IncidentsSectionHeader
        statusFilter={statusFilter}
        incidentCount={filteredAndSortedIncidents.length}
      />

      {filteredAndSortedIncidents.length === 0 ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {statusFilter === "active"
            ? "No active long running task errors."
            : "No long running tasks match the current filters."}
        </Alert>
      ) : (
        <Box sx={{ height: "calc(100% - 360px)", minHeight: 300 }}>
          <IncidentsTable
            incidents={filteredAndSortedIncidents}
            monitorColumnLabel="Table name"
            renderTaskStatus={(incident) => incident.taskStatus}
            renderAction={(incident) => (
              <Button
                size="small"
                variant="text"
                color="primary"
                disabled={
                  incident.isResolved || completingTaskId === incident.guid
                }
                onClick={() => handleOpenCompleteDialog(incident)}
                sx={{
                  minWidth: "auto",
                  p: 0,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {completingTaskId === incident.guid ? "Completing" : "Complete"}
              </Button>
            )}
          />
        </Box>
      )}

      <CompleteLongRunningTaskDialog
        task={selectedTask}
        isCompleting={Boolean(completingTaskId)}
        onClose={handleCloseCompleteDialog}
        onComplete={handleCompleteTask}
      />
    </Box>
  );
};

export default LongRunningTasksStatus;
